'use client';

import { createContext, useContext } from 'react';
import { ChannelProvider } from 'ably/react';

import { useInfiniteNotifications } from '@/hooks/use-infinite-notifications';
import type { User } from '@/types/auth';
import type { Notification } from '@/types/notifications';

type NotificationContextType = {
  getCounts: () => { all: number; messages: number; general: number };
  notifications: Notification[];
  isLoading: boolean;
  isLoadingMore: boolean;
  hasMore: boolean;
  loadMoreRef: React.RefObject<HTMLDivElement | null>;
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  removeNotification: (id: string) => Promise<void>;
  setScrollContainerRef: (element: HTMLDivElement | null) => void;
};

const NotificationContext = createContext<NotificationContextType | undefined>(
  undefined,
);

export const useNotificationContext = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error(
      'useNotificationContext must be used within NotificationProvider',
    );
  }
  return context;
};

type NotificationProviderProps = {
  children: React.ReactNode;
  user: User;
};

function NotificationProviderContent({
  children,
  user,
}: NotificationProviderProps) {
  const notificationHook = useInfiniteNotifications(user?.id, 10);

  const contextValue: NotificationContextType = {
    getCounts: notificationHook.getCounts,
    notifications: notificationHook.notifications,
    isLoading: notificationHook.isLoading,
    isLoadingMore: notificationHook.isLoadingMore,
    hasMore: notificationHook.hasMore,
    loadMoreRef: notificationHook.loadMoreRef,
    markAsRead: notificationHook.markAsRead,
    markAllAsRead: notificationHook.markAllAsRead,
    removeNotification: notificationHook.removeNotification,
    setScrollContainerRef: notificationHook.setScrollContainerRef,
  };

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
    </NotificationContext.Provider>
  );
}

export default function NotificationProvider({
  children,
  user,
}: NotificationProviderProps) {
  const notificationChannelId = `user-${user.id}-notifications`;

  return (
    <ChannelProvider channelName={notificationChannelId}>
      <NotificationProviderContent user={user}>
        {children}
      </NotificationProviderContent>
    </ChannelProvider>
  );
}
